import React from 'react';
import { Plus, X, Package, ListChecks } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { SearchPicker } from '../SearchPicker';

const KitRows = ({ rows, onChange, itemOptions, testId }) => (
  <div className="space-y-1.5">
    {rows.map((r, i) => (
      <div key={i} className="flex items-center gap-2" data-testid={`${testId}-row-${i}`}>
        <div className="flex-1 min-w-0">
          <SearchPicker value={r.item_sku} options={itemOptions} placeholder="— pick item —" testId={`${testId}-item-${i}`}
            onChange={(v) => onChange(rows.map((x, j) => (j === i ? { ...x, item_sku: v || '' } : x)))} />
        </div>
        <Input type="number" min="0.01" step="0.01" value={r.quantity} className="w-20 h-9 text-xs text-right mono rounded-sm" data-testid={`${testId}-qty-${i}`}
          onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))} />
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => onChange(rows.filter((_, j) => j !== i))} data-testid={`${testId}-remove-${i}`}><X className="w-3.5 h-3.5" /></Button>
      </div>
    ))}
    <Button type="button" variant="outline" size="sm" className="h-7 text-xs rounded-sm" onClick={() => onChange([...rows, { item_sku: '', quantity: 1 }])} data-testid={`${testId}-add`}><Plus className="w-3 h-3 mr-1" />Add item</Button>
  </div>
);

// Product "what's in the box" + option groups editor.
export const BoxListEditor = ({ boxItems, optionGroups, bundlePricing, onChange, items }) => {
  const itemOptions = items.filter(it => it.item_sku).map(it => ({ value: it.item_sku, label: `${it.item_sku} — ${it.item_name}` }));
  const setGroups = (g) => onChange({ option_groups: g });
  const setGroup = (i, patch) => setGroups(optionGroups.map((g, j) => (j === i ? { ...g, ...patch } : g)));
  return (
    <div className="space-y-4 rounded-sm border border-slate-200 p-3 bg-slate-50/50" data-testid="box-list-editor">
      <div className="space-y-2">
        <Label className="flex items-center gap-1.5"><Package className="w-4 h-4 text-orange-600" />What's in the box</Label>
        <p className="text-[11px] text-slate-500">Added automatically when this product goes on a sales order or packing slip.</p>
        <KitRows rows={boxItems} onChange={(r) => onChange({ box_items: r })} itemOptions={itemOptions} testId="box-item" />
        <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer pt-1">
          <Switch checked={!!bundlePricing} onCheckedChange={(v) => onChange({ bundle_pricing: v })} data-testid="bundle-pricing-switch" />
          <span><b>Bundle pricing</b> — the product price includes the box items, so they go on sales orders at $0. Option picks (e.g. pump upgrades) keep their own price.</span>
        </label>
      </div>
      <div className="space-y-2">
        <Label className="flex items-center gap-1.5"><ListChecks className="w-4 h-4 text-orange-600" />Option groups</Label>
        <p className="text-[11px] text-slate-500">One choice is picked per group on each order, e.g. "Vacuum pump".</p>
        {optionGroups.map((g, i) => (
          <div key={i} className="rounded-sm border border-slate-200 bg-white p-2 space-y-2" data-testid={`option-group-${i}`}>
            <div className="flex items-center gap-2">
              <Input value={g.name} onChange={(e) => setGroup(i, { name: e.target.value })} placeholder="Group name, e.g. Vacuum pump" className="h-8 text-xs rounded-sm" data-testid={`option-group-name-${i}`} />
              <label className="flex items-center gap-1 text-[11px] text-slate-600 whitespace-nowrap"><Switch checked={g.required !== false} onCheckedChange={(v) => setGroup(i, { required: v })} data-testid={`option-group-required-${i}`} />Required</label>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => setGroups(optionGroups.filter((_, j) => j !== i))} data-testid={`option-group-remove-${i}`}><X className="w-3.5 h-3.5" /></Button>
            </div>
            <KitRows rows={g.choices || []} onChange={(r) => setGroup(i, { choices: r })} itemOptions={itemOptions} testId={`option-group-${i}-choice`} />
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs rounded-sm" onClick={() => setGroups([...optionGroups, { name: '', required: true, choices: [{ item_sku: '', quantity: 1 }] }])} data-testid="option-group-add"><Plus className="w-3 h-3 mr-1" />Add option group</Button>
      </div>
    </div>
  );
};
